import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CircleNotch } from '@phosphor-icons/react'
import type { GameItem, GameItemType } from '@/types'
import { ITEM_TYPES } from '@/lib/itemTypes'

interface EditItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  itemData: GameItem | null
  onSave: (itemData: Omit<GameItem, 'id'> & { id?: string }) => Promise<void>
}

// Emojis comuns para itens/artefatos
const COMMON_EMOJIS = [
  '🎁', '🏆', '🏅', '🎖️', '👑', '💎', '🔑', '🗝️',
  '⚔️', '🛡️', '🏹', '🪄', '📜', '📖', '🧪', '⚗️',
  '🔬', '🔭', '🧭', '🗺️', '💊', '🧰', '🔮', '⭐',
]

const DEFAULT_TYPE: GameItemType = 'trofeu'

export function EditItemDialog({ open, onOpenChange, itemData, onSave }: EditItemDialogProps) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    icon: '🎁',
    item_type: DEFAULT_TYPE as GameItemType,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  const isEditing = !!itemData

  useEffect(() => {
    if (itemData) {
      setFormData({
        id: itemData.id,
        name: itemData.name,
        description: itemData.description,
        icon: itemData.icon,
        item_type: itemData.item_type,
      })
    } else {
      setFormData({ id: '', name: '', description: '', icon: '🎁', item_type: DEFAULT_TYPE })
    }
  }, [itemData, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const itemId = formData.id || formData.name.toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')

    setIsSaving(true)
    try {
      await onSave({
        ...(isEditing && { id: formData.id }),
        ...(!isEditing && { id: itemId }),
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        item_type: formData.item_type,
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Erro ao salvar item:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar Item' : 'Novo Item'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Modifique as informações do item do catálogo.'
                : 'Crie um item para o catálogo da campanha.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Ícone */}
            <div className="space-y-2">
              <Label htmlFor="icon">Ícone</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="text-2xl w-16 h-16"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                  {formData.icon}
                </Button>
                <Input
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Cole um emoji aqui"
                  className="flex-1"
                />
              </div>

              {showEmojiPicker && (
                <div className="grid grid-cols-8 gap-1 p-2 border rounded-md bg-background">
                  {COMMON_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="text-2xl p-2 hover:bg-accent rounded transition-colors"
                      onClick={() => {
                        setFormData({ ...formData, icon: emoji })
                        setShowEmojiPicker(false)
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Item *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Prisma de Newton, Chave Mestra..."
                required
              />
            </div>

            {/* Tipo */}
            <div className="space-y-2">
              <Label htmlFor="item_type">Tipo *</Label>
              <select
                id="item_type"
                value={formData.item_type}
                onChange={(e) => setFormData({ ...formData, item_type: e.target.value as GameItemType })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {ITEM_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                {ITEM_TYPES.find((t) => t.value === formData.item_type)?.description}
              </p>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva o item..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <CircleNotch size={16} className="mr-2 animate-spin" />}
              {isEditing ? 'Salvar Alterações' : 'Criar Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
